import { GetServerSidePropsContext } from 'next';
import { formatVersatileCard } from '@/utils/contentful-data-formatters';
import { VersatileCardResponse } from '@/utils/contentful-data-formatters/contentful-data-formatters.types';
import {
  PartnerInfoProperties,
  PartnerInfoQueryResponse,
} from './partner-info.types';
import Partner from '../../../models/partner';
import { partnerInfoQuery } from './partner-info.query';
import { arrayToObject } from '../../../utils/array-to-object';
import ContentfulProvider from '../../../providers/contentful';
import { formatWasRate } from '../../../utils/format-was-rate';

export const partnerInfoHandler = async (
  { req: { opco } }: GetServerSidePropsContext,
  partnerSlug: string,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<PartnerInfoProperties | null> => {
  const contentfulRequest = new ContentfulProvider(
    opco,
  ).makeContentfulRequest<PartnerInfoQueryResponse>({
    query: partnerInfoQuery,
  });

  const partner = new Partner(opco, partnerSlug);

  await partner.isReady();
  if (!partner.isValid) return null;

  if (Object.keys(partner.collectionMethods).length === 0)
    throw new Error('Partner Not Found');

  await partner.getRelatedPartners();

  const {
    slug,
    logoSrc,
    heroSrc,
    description,
    name,
    collectionMethods,
    id,
    mechanicId,
    destinationUrl,
    externalReferences,
    category,
  } = partner;

  const contentfulResponse = await contentfulRequest;

  const contentfulData = contentfulResponse.hasError
    ? null
    : contentfulResponse.data;

  const content = arrayToObject(
    contentfulData?.microcopyCollection?.items ?? [],
  );

  const aviosTimelineInfoContent =
    contentfulData?.pagePartnerCollection?.items[0]?.aviosTimelineInfoModal ??
    null;

  const formattedAviosTimelineInfoContent = formatVersatileCard(
    aviosTimelineInfoContent as VersatileCardResponse,
  );

  const responseObject = {
    slug,
    externalReferences,
    id,
    mechanicId,
    name,
    destinationUrl,
    breadcrumbs: {
      collectText: content['merchant.collect'] ?? null,
      partnerCategory: category,
    },
    pageTitle: `${name} | ${content['merchant.pageTitle'] ?? ''}`,
    title: `${content['merchant.titleHeading'] ?? ''} ${name}`,
    subtitle: content['merchant.whatYouNeedToKnow'] ?? null,
    hero: {
      src: heroSrc,
      alt: '',
    },
    errorLabels: {
      title: content['merchant.error.title'] ?? null,
      message: content['merchant.error.message'] ?? null,
      close: content['general.closeLabel'] ?? null,
    },
    favouritesLabels: {
      addToFavourites: content['favourites.button.addToFavourites'] ?? null,
      addedToFavourites: content['favourites.button.addedToFavourites'] ?? null,
      removeFromFavourites:
        content['favourites.button.removeFromFavourites'] ?? null,
      removedFromFavourites:
        content['favourites.button.removedFromFavourites'] ?? null,
    },
    card: {
      logo: {
        src: logoSrc,
        alt: 'TODO',
      },
      speedyAwardText: `${content['speedyAward.title'] ?? ''}`,
    },
    about: {
      title: `${content['merchant.about'] ?? ''} ${name}`,
      text: description,
    },
    purchaseConditions: {},
    rates: {},
    terms: {
      backToTopLabel: content['termsandconditions.backToTop'] ?? null,
      openLabel: content['termsandconditions.label'] ?? null,
    },
    tabs: {},
    shopNowLoggedOutCtaLabel: content['shopNow.loggedOutCtaLabel'],
  } as PartnerInfoProperties;

  for (const [collectionKey, mechanic] of Object.entries(collectionMethods)) {
    const {
      ctaText,
      isSpeedyAwarding,
      purchaseConditions,
      additionalPurchaseConditions,
      rate,
      tiers,
      wasRate,
      termsAndConditions,
      termsAndConditionsTitle,
      ctaLink,
      trackedTimelineUnit,
      trackedTimelinePeriod,
      awardedTimelineUnit,
      awardedTimelinePeriod,
    } = mechanic;

    responseObject.card = {
      ...responseObject.card,
      [collectionKey]: {
        isSpeedyAwarding,
        rate,
        wasRate:
          wasRate && formatWasRate(wasRate, content['merchant.ratewas'] ?? ''),
        cta: {
          target: ctaLink?.target,
          link: ctaLink?.url,
          text: ctaText,
          ctaTextOverwrite: partner.ctaTextOverwrite(collectionKey) ?? null,
        },
      },
    };
    responseObject.purchaseConditions = {
      ...responseObject.purchaseConditions,
      [collectionKey]: {
        text: purchaseConditions,
        highlighted: additionalPurchaseConditions,
      },
    };
    responseObject.rates = {
      ...responseObject.rates,
      [collectionKey]: {
        title: content['purchasehistory.avios'] ?? '',
        value: tiers,
      },
    };
    responseObject.terms = {
      ...responseObject.terms,
      [collectionKey]: {
        title: termsAndConditionsTitle,
        text: termsAndConditions,
      },
    };
    responseObject.tabs = {
      ...responseObject.tabs,
      [collectionKey]: {
        tabTitle:
          content[`mechanic.tab.title.${collectionKey.toLocaleLowerCase()}`] ??
          '',
      },
    };
    responseObject.aviosTimeline = {
      purchasedLabel: content['pagePartner.aviosTimeline.purchased'] ?? null,
      awardedLabel: content['pagePartner.aviosTimeline.awarded'] ?? null,
      daysLabel: content['general.time.days'] ?? null,
      hoursLabel: content['general.time.hours'] ?? null,
      inPendingLabel: content['general.status.inPending'] ?? null,
      withinLabel: content['general.within'] ?? null,
      todayLabel: content['general.time.today'] ?? null,
      headlineLabel: content['pagePartner.aviosTimeline.headline'] ?? null,
      moreInfoLabel: content['pagePartner.aviosTimeline.moreInfoLabel'] ?? null,
      trackedTimelineUnit,
      trackedTimelinePeriod,
      awardedTimelineUnit,
      awardedTimelinePeriod,
      infoModalContent: formattedAviosTimelineInfoContent,
    };
  }

  return responseObject;
};
